import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Image,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table as TiptapTable } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Image as ImageExtension } from "@tiptap/extension-image";
import { TextAlign } from "@tiptap/extension-text-align";
import { Underline } from "@tiptap/extension-underline";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { useForm } from "@mantine/form";
import {
  IconAlignCenter,
  IconAlignJustified,
  IconAlignLeft,
  IconAlignRight,
  IconPhoto,
  IconTable,
  IconUnderline,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { ITipProps } from "../../../lib/types/Tip";
import {
  createTip,
  deleteTip,
  getTips,
  updateTip,
} from "../../../services/tips";
import { uploadImage } from "../../../services/upload";

type TipFormValues = {
  title: string;
  body: string;
  image?: string;
  activeFrom: Date | null;
  expiresAt: Date | null;
};

export default function TipsPage() {
  const [tips, setTips] = useState<ITipProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<ITipProps | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const form = useForm<TipFormValues>({
    initialValues: {
      title: "",
      body: "",
      image: undefined,
      activeFrom: null,
      expiresAt: null,
    },
    validate: {
      title: (v) => (v.trim().length ? null : "Title is required"),
      body: (v) => (v.trim().length ? null : "Body is required"),
      activeFrom: (v) => (v ? null : "Active date is required"),
      expiresAt: (v) => (v ? null : "Expiration date is required"),
    },
  });

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link,
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TiptapTable.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      ImageExtension,
    ],
    content: form.values.body,
    onUpdate: ({ editor }) => {
      form.setFieldValue("body", editor.getHTML());
    },
  });

  // Sync editor content when form values change (specifically for reset)
  useEffect(() => {
    if (editor && !editing && form.values.body === "") {
      editor.commands.setContent("");
    }
  }, [modalOpen, editing, editor, form.values.body]);

  async function load() {
    setLoading(true);
    try {
      const data = await getTips(1, 100); // Fetch up to 100 tips
      setTips(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleImageUpload(file: File) {
    setUploading(true);
    try {
      const result = await uploadImage(file, "tips");
      form.setFieldValue("image", result.url);
    } catch (error) {
      console.error("Failed to upload image:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  }

  async function handleInlineImageUpload(
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadImage(file, "tips");
      (editor?.chain().focus() as any).setImage({ src: result.url }).run();
    } catch (error) {
      console.error("Failed to upload inline image:", error);
      alert("Failed to upload inserted image. Please try again.");
    } finally {
      // Clear input
      if (fileInputRef.current) fileInputRef.current.value = "";
      setUploading(false);
    }
  }

  function openCreate() {
    setEditing(null);
    form.setValues({
      title: "",
      body: "",
      image: undefined,
      activeFrom: new Date(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // Default 1 week
    });
    editor?.commands.setContent("");
    setModalOpen(true);
  }

  function openEdit(t: ITipProps) {
    setEditing(t);
    form.setValues({
      title: t.title,
      body: t.body,
      image: t.image,
      activeFrom: new Date(t.activeFrom),
      expiresAt: new Date(t.expiresAt),
    });
    editor?.commands.setContent(t.body || "");
    setModalOpen(true);
  }

  async function handleSave() {
    const valid = form.validate();
    if (valid.hasErrors) return;

    setLoading(true);
    const values: any = {
      title: form.values.title,
      body: form.values.body,
      activeFrom: new Date(form.values.activeFrom!).getTime(),
      expiresAt: new Date(form.values.expiresAt!).getTime(),
    };

    if (form.values.image) {
      values.image = form.values.image;
    }

    try {
      if (editing) {
        const updated = await updateTip(editing.id, values);
        if (updated) {
          setTips((prev) =>
            prev.map((t) => (t.id === updated.id ? updated : t)),
          );
        }
      } else {
        const created = await createTip(values);
        setTips((prev) => [created, ...prev]);
      }
      setModalOpen(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm("Are you sure you want to delete this tip?")) return;
    setLoading(true);
    try {
      const ok = await deleteTip(id);
      if (ok) setTips((prev) => prev.filter((t) => t.id !== id));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  const getStatus = (activeFrom: number, expiresAt: number) => {
    const now = Date.now();
    if (now > expiresAt) return <Badge color="gray">Expired</Badge>;
    if (now < activeFrom) return <Badge color="blue">Scheduled</Badge>;
    return <Badge color="green">Active</Badge>;
  };

  const rows = useMemo(
    () =>
      tips.map((t) => (
        <Table.Tr key={t.id}>
          <Table.Td>{t.title}</Table.Td>
          <Table.Td>{new Date(t.activeFrom).toLocaleDateString()}</Table.Td>
          <Table.Td>{new Date(t.expiresAt).toLocaleDateString()}</Table.Td>
          <Table.Td>{getStatus(t.activeFrom, t.expiresAt)}</Table.Td>
          <Table.Td>
            <Group gap="xs">
              <Button
                size="xs"
                variant="light"
                radius="sm"
                onClick={() => openEdit(t)}
              >
                Edit
              </Button>
              <Button
                size="xs"
                color="red"
                radius="sm"
                onClick={() => handleDelete(t.id)}
              >
                Delete
              </Button>
            </Group>
          </Table.Td>
        </Table.Tr>
      )),
    [tips],
  );

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Weekly Tips</Title>
        <Button variant="default" radius="sm" onClick={openCreate}>
          New Tip
        </Button>
      </Group>

      <Paper p="md" radius="sm" shadow="xs">
        {tips.length === 0 ? (
          <Text c="dimmed">No tips yet. Create your first weekly tip.</Text>
        ) : (
          <Table stickyHeader striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Title</Table.Th>
                <Table.Th>Active From</Table.Th>
                <Table.Th>Expires At</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
        )}
      </Paper>

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? "Edit Tip" : "New Tip"}
        centered
        size="xl"
      >
        <Stack>
          <TextInput
            label="Title"
            placeholder="Weekly health insight"
            radius="sm"
            {...form.getInputProps("title")}
          />
          <Stack gap={6}>
            <Text size="sm" fw={500}>
              Image (Optional)
            </Text>
            {form.values.image ? (
              <Stack gap="xs">
                <Image
                  src={form.values.image}
                  alt="Tip preview"
                  radius="sm"
                  maw={400}
                  mah={300}
                  fit="cover"
                />
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="light"
                    color="red"
                    onClick={() => form.setFieldValue("image", undefined)}
                  >
                    Remove Image
                  </Button>
                </Group>
              </Stack>
            ) : (
              <Dropzone
                onDrop={(files) => {
                  if (files[0]) {
                    handleImageUpload(files[0]);
                  }
                }}
                accept={IMAGE_MIME_TYPE}
                maxSize={5 * 1024 * 1024} // 5MB
                loading={uploading}
                disabled={uploading}
              >
                <Group
                  justify="center"
                  gap="xl"
                  mih={100}
                  style={{ pointerEvents: "none" }}
                >
                  <Dropzone.Accept>
                    <IconUpload size={52} stroke={1.5} />
                  </Dropzone.Accept>
                  <Dropzone.Reject>
                    <IconX size={52} stroke={1.5} />
                  </Dropzone.Reject>
                  <Dropzone.Idle>
                    <IconPhoto size={52} stroke={1.5} />
                  </Dropzone.Idle>

                  <div>
                    <Text size="xl" inline>
                      Drag image here or click to select
                    </Text>
                    <Text size="sm" c="dimmed" inline mt={7}>
                      Attach an image file for this tip (max 5MB)
                    </Text>
                  </div>
                </Group>
              </Dropzone>
            )}
          </Stack>
          <Group grow>
            <DateTimePicker
              label="Active From"
              placeholder="Pick date and time"
              {...form.getInputProps("activeFrom")}
            />
            <DateTimePicker
              label="Expires At"
              placeholder="Pick date and time"
              {...form.getInputProps("expiresAt")}
            />
          </Group>
          <Stack gap={6}>
            <Text size="sm">Body</Text>
            <RichTextEditor editor={editor}>
              <RichTextEditor.Toolbar sticky stickyOffset={0}>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Bold />
                  <RichTextEditor.Italic />
                  <RichTextEditor.Underline />
                  <RichTextEditor.Strikethrough />
                  <RichTextEditor.ClearFormatting />
                  <RichTextEditor.ColorPicker
                    colors={[
                      "#25262b",
                      "#868e96",
                      "#fa5252",
                      "#e64980",
                      "#be4bdb",
                      "#7950f2",
                      "#4c6ef5",
                      "#228be6",
                      "#15aabf",
                      "#12b886",
                      "#40c057",
                      "#82c91e",
                      "#fab005",
                      "#fd7e14",
                    ]}
                  />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.H1 />
                  <RichTextEditor.H2 />
                  <RichTextEditor.H3 />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.AlignLeft />
                  <RichTextEditor.AlignCenter />
                  <RichTextEditor.AlignJustify />
                  <RichTextEditor.AlignRight />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Blockquote />
                  <RichTextEditor.Hr />
                  <RichTextEditor.BulletList />
                  <RichTextEditor.OrderedList />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Link />
                  <RichTextEditor.Unlink />
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Control
                    onClick={() => {
                      fileInputRef.current?.click();
                    }}
                    title="Insert image from computer"
                  >
                    <IconPhoto size="1rem" stroke={1.5} />
                  </RichTextEditor.Control>
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Control
                    onClick={() =>
                      (editor?.chain().focus() as any)
                        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                        .run()
                    }
                    title="Insert table"
                  >
                    <IconTable size="1rem" stroke={1.5} />
                  </RichTextEditor.Control>
                  <RichTextEditor.Control
                    onClick={() =>
                      (editor?.chain().focus() as any).addColumnBefore().run()
                    }
                    title="Add column before"
                  >
                    <IconTable size="1rem" stroke={1.5} />
                  </RichTextEditor.Control>
                  <RichTextEditor.Control
                    onClick={() =>
                      (editor?.chain().focus() as any).addColumnAfter().run()
                    }
                    title="Add column after"
                  >
                    <IconTable size="1rem" stroke={1.5} />
                  </RichTextEditor.Control>
                  <RichTextEditor.Control
                    onClick={() =>
                      (editor?.chain().focus() as any).deleteColumn().run()
                    }
                    title="Delete column"
                  >
                    <IconTable size="1rem" stroke={1.5} color="red" />
                  </RichTextEditor.Control>
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Control
                    onClick={() =>
                      (editor?.chain().focus() as any).addRowBefore().run()
                    }
                    title="Add row before"
                  >
                    <IconTable size="1rem" stroke={1.5} />
                  </RichTextEditor.Control>
                  <RichTextEditor.Control
                    onClick={() =>
                      (editor?.chain().focus() as any).addRowAfter().run()
                    }
                    title="Add row after"
                  >
                    <IconTable size="1rem" stroke={1.5} />
                  </RichTextEditor.Control>
                  <RichTextEditor.Control
                    onClick={() =>
                      (editor?.chain().focus() as any).deleteRow().run()
                    }
                    title="Delete row"
                  >
                    <IconTable size="1rem" stroke={1.5} color="red" />
                  </RichTextEditor.Control>
                </RichTextEditor.ControlsGroup>
                <RichTextEditor.ControlsGroup>
                  <RichTextEditor.Control
                    onClick={() =>
                      (editor?.chain().focus() as any).deleteTable().run()
                    }
                    title="Delete table"
                  >
                    <IconX size="1rem" stroke={1.5} color="red" />
                  </RichTextEditor.Control>
                </RichTextEditor.ControlsGroup>
              </RichTextEditor.Toolbar>
              <RichTextEditor.Content mih={160} />
            </RichTextEditor>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleInlineImageUpload}
            />
          </Stack>
          <Group justify="flex-end">
            <Button
              variant="default"
              radius="sm"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </Button>
            <Button radius="sm" loading={loading} onClick={handleSave}>
              {editing ? "Save Changes" : "Create Tip"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
